import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useFmMotion } from './FmMotionHelper';
import { FmAccordion } from './FmAccordion';
import { FmButton } from './FmButton';
import type { FmProductRegisteredMediaSection } from './FmProductPage.types';
import {
  getMediaAsset,
  getMediaDeliveryRevision,
  subscribeMediaDelivery,
} from '@/shared/config/mediaRegistry';
import './FmAssetVideo.css';

type VideoSection = Extract<
  FmProductRegisteredMediaSection,
  { mediaKind: 'video'; answer: string }
>;

function useNarrowFilm(): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 48rem)').matches,
  );
  useEffect(() => {
    const query = window.matchMedia('(max-width: 48rem)');
    const update = () => setNarrow(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return narrow;
}

export const FmAssetVideo: React.FC<{ section: VideoSection }> = ({ section }) => {
  useSyncExternalStore(subscribeMediaDelivery, getMediaDeliveryRevision, getMediaDeliveryRevision);
  const { prefersReducedMotion } = useFmMotion();
  const narrow = useNarrowFilm();
  const frameRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [nearViewport, setNearViewport] = useState(false);
  const [inView, setInView] = useState(false);
  const [manualPause, setManualPause] = useState(false);
  const [playing, setPlaying] = useState(false);
  const mediaIdentity =
    narrow && section.mobileMediaIdentity ? section.mobileMediaIdentity : section.mediaIdentity;
  const posterIdentity =
    narrow && section.mobilePosterIdentity ? section.mobilePosterIdentity : section.posterIdentity;
  const media = getMediaAsset(mediaIdentity);
  const poster = getMediaAsset(posterIdentity);
  const canAutoplay = media.autoplay === true;

  const playWhenEligible = useCallback(() => {
    const video = videoRef.current;
    if (!video || !canAutoplay || !inView || prefersReducedMotion || manualPause) return;
    void video.play().catch(() => undefined);
  }, [canAutoplay, inView, manualPause, prefersReducedMotion]);

  useEffect(() => {
    setManualPause(false);
    setPlaying(false);
    setNearViewport(false);
  }, [mediaIdentity]);

  useEffect(() => {
    if (prefersReducedMotion && videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
    }
  }, [prefersReducedMotion]);

  useEffect(playWhenEligible, [playWhenEligible]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setNearViewport(true);
        const sufficientlyVisible = entry.intersectionRatio >= 0.55;
        setInView(sufficientlyVisible);
        const video = videoRef.current;
        if (!video) return;
        if (!sufficientlyVisible && !video.paused) {
          video.pause();
        }
      },
      { rootMargin: '25% 0%', threshold: [0, 0.55] },
    );
    observer.observe(frame);
    return () => observer.disconnect();
  }, [mediaIdentity]);

  return (
    <div
      className="fm-product-film"
      data-media-identity={mediaIdentity}
      data-poster-identity={posterIdentity}
      data-film-layout={narrow && section.mobileMediaIdentity ? 'portrait' : 'wide'}
      data-reduced-motion={prefersReducedMotion ? 'true' : 'false'}
    >
      <div className="fm-product-film__copy">
        <h3 className="fm-product-answer">{section.answer}</h3>
        {section.explanation ? (
          <p className="fm-product-explanation">{section.explanation}</p>
        ) : null}
      </div>
      <figure ref={frameRef} className="fm-product-film__figure">
        <div className="fm-product-film__frame">
          <video
            key={mediaIdentity}
            ref={videoRef}
            className="fm-product-film__video"
            loop={media.loop === true}
            muted
            playsInline
            poster={poster.src}
            preload={nearViewport ? 'metadata' : 'none'}
            src={nearViewport ? media.src : undefined}
            aria-label={section.playLabel}
            onCanPlay={playWhenEligible}
            onPause={() => setPlaying(false)}
            onPlay={() => setPlaying(true)}
          >
            {section.playLabel}
          </video>
          <FmButton
            variant="text"
            className="fm-product-film__playback"
            aria-label={playing ? section.pauseLabel : section.playLabel}
            title={playing ? section.pauseLabel : section.playLabel}
            onClick={() => {
              const video = videoRef.current;
              if (!video) return;
              if (video.paused) {
                setManualPause(false);
                void video.play().catch(() => undefined);
              } else {
                setManualPause(true);
                video.pause();
              }
            }}
          >
            <span aria-hidden="true">{playing ? 'Ⅱ' : '▶'}</span>
          </FmButton>
        </div>
        <figcaption>{section.caption}</figcaption>
      </figure>
      <FmAccordion
        plain
        className="fm-product-film__transcript"
        items={[
          {
            key: 'transcript',
            label: section.transcriptLabel,
            children: (
              <div className="fm-product-film__transcript-body">
                <h4>{section.transcriptTitle}</h4>
                <ol>
                  {section.transcriptScenes.map((scene) => (
                    <li key={scene.id}>
                      <time>{scene.timeLabel}</time>
                      <div>
                        <strong>{scene.title}</strong>
                        <p>{scene.detail}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};
